// ============================================================================
// 全国の主要花火大会データ
// ----------------------------------------------------------------------------
// lat/lngは手入力ではなく、venueQuery（会場名の検索文字列）をNominatimで
// ジオコーディングした結果をそのまま保存したもの。理由：主要ランドマークの
// 緯度経度を手入力で決め打ちすると誤差や思い込みが混入しやすく、地図サービス
// 上の実際の地名検索のほうが信頼できるため（venueQueryは再検証・再取得用に
// 残してある）。都度ジオコーディングではなく保存済みの値を使うのは、地図上に
// 全大会のピンを常設表示する機能で、選択前から座標が必要になるため。
//
// 打ち上げ高度(estimatedAltitude)は公式発表が無いことが多く、公表されている
// 「玉の号数」から標準的な目安（2.5号≈50m、10号(尺玉)≈300m、号数が大きいほど
// 開花高度が上がる）を根拠に推定した参考値。実測値ではない。
//
// 基準は「その大会で象徴的・特徴的な号数」（shellInfoの「〜中心」「〜あり」
// 「〜も打ち上げられる」等の記述で言及されている、最も目立つクラス）。
// この値はUI上の高度表示だけでなく、①建物の陰に隠れるかの物理計算(打ち上げ
// 高度そのもの)、②解析範囲(肉眼で十分見える限界距離)の両方に直結するため、
// 「一番頻繁に上がる号数(=つなぎの小玉)」を使うと、その大会の目玉である
// 尺玉等がどこまで見えるかを過小評価してしまう。実際の見えやすさの現実的な
// 割引は、号数選びではなくshellSizes.tsの光害・霞み係数(HAZE_DISCOUNT_FACTOR)
// 側で一括して行う。
// ============================================================================

export interface Festival {
  id: string;
  name: string;
  prefecture: string;
  /** Nominatimでジオコーディングした際の検索文字列(再検証・再取得用)。 */
  venueQuery: string;
  /** 会場の緯度経度(Nominatimでのジオコーディング結果)。 */
  lat: number;
  lng: number;
  /** 開催時期の目安。 */
  season: string;
  /** 花火玉の規模の目安。 */
  shellInfo: string;
  /** 打ち上げ高度の目安(m)。号数からの推定値。 */
  estimatedAltitude: number;
  note?: string;
}

export const ALL_FESTIVALS: Festival[] = [
  {
    id: "sumidagawa",
    name: "隅田川花火大会（第一会場）",
    prefecture: "東京都",
    venueQuery: "桜橋 東京都墨田区",
    lat: 35.7174438,
    lng: 139.8067384,
    season: "7月下旬",
    shellInfo: "中小玉中心（都心部のため大玉は少なめ）",
    estimatedAltitude: 160,
    note: "第一会場(桜橋〜言問橋)を想定。",
  },
  {
    id: "sumidagawa2",
    name: "隅田川花火大会（第二会場）",
    prefecture: "東京都",
    venueQuery: "駒形橋 東京都台東区",
    lat: 35.7082414,
    lng: 139.7974419,
    season: "7月下旬",
    shellInfo: "中小玉中心（都心部のため大玉は少なめ）",
    estimatedAltitude: 160,
    note: "第二会場(駒形橋〜厩橋)を想定。",
  },
  {
    id: "edogawa",
    name: "江戸川区花火大会",
    prefecture: "東京都",
    venueQuery: "都立篠崎公園 東京都江戸川区",
    lat: 35.7083991,
    lng: 139.8959179,
    season: "8月上旬",
    shellInfo: "尺玉（10号）あり",
    estimatedAltitude: 300,
  },
  {
    id: "itabashi",
    name: "いたばし花火大会",
    prefecture: "東京都",
    venueQuery: "舟渡 東京都板橋区",
    lat: 35.7941546,
    lng: 139.6837601,
    season: "8月上旬",
    shellInfo: "尺玉の連続打ち上げで知られる",
    estimatedAltitude: 300,
  },
  {
    id: "jinguGaien",
    name: "神宮外苑花火大会",
    prefecture: "東京都",
    venueQuery: "明治神宮外苑 東京都新宿区",
    lat: 35.6762880,
    lng: 139.7183406,
    season: "8月上旬",
    shellInfo: "都心部のため小玉中心",
    estimatedAltitude: 120,
  },
  {
    id: "tsuchiura",
    name: "土浦全国花火競技大会",
    prefecture: "茨城県",
    venueQuery: "学園大橋 茨城県土浦市",
    lat: 36.0872631,
    lng: 140.1805397,
    season: "11月第1土曜（秋開催）",
    shellInfo: "10号玉(尺玉)中心の全国競技大会。日本三大花火大会の一つ",
    estimatedAltitude: 330,
  },
  {
    id: "mito",
    name: "水戸黄門まつり花火大会",
    prefecture: "茨城県",
    venueQuery: "千波湖 茨城県水戸市",
    lat: 36.3700002,
    lng: 140.4611091,
    season: "7月下旬〜8月上旬",
    shellInfo: "中型玉中心",
    estimatedAltitude: 200,
  },
  {
    id: "makuhari",
    name: "幕張ビーチ花火フェスタ",
    prefecture: "千葉県",
    venueQuery: "幕張海浜公園 千葉県千葉市",
    lat: 35.6487931,
    lng: 140.0446585,
    season: "8月上旬",
    shellInfo: "海浜公園のため比較的大玉も可能",
    estimatedAltitude: 200,
  },
  {
    id: "saitama",
    name: "さいたま市花火大会",
    prefecture: "埼玉県",
    venueQuery: "大和田公園 さいたま市北区",
    lat: 35.9234260,
    lng: 139.6404791,
    season: "7月下旬",
    shellInfo: "中型玉中心",
    estimatedAltitude: 200,
  },
  {
    id: "koshigaya",
    name: "越谷花火大会",
    prefecture: "埼玉県",
    venueQuery: "越谷市大相模調節池",
    lat: 35.8772108,
    lng: 139.8193642,
    season: "7月下旬〜8月上旬",
    shellInfo: "中型玉中心",
    estimatedAltitude: 200,
  },
  {
    id: "yokohama",
    name: "横浜開港祭花火大会",
    prefecture: "神奈川県",
    venueQuery: "横浜赤レンガ倉庫 神奈川県横浜市",
    lat: 35.4524046,
    lng: 139.6429182,
    season: "6月上旬",
    shellInfo: "港湾部の都市型花火",
    estimatedAltitude: 180,
  },
  {
    id: "kamakura",
    name: "鎌倉花火大会",
    prefecture: "神奈川県",
    venueQuery: "由比ガ浜",
    lat: 35.3137738,
    lng: 139.5410038,
    season: "7月下旬",
    shellInfo: "海上の台船から打ち上げ",
    estimatedAltitude: 160,
  },
  {
    id: "ashikaga",
    name: "足利花火大会",
    prefecture: "栃木県",
    venueQuery: "渡良瀬橋 栃木県足利市",
    lat: 36.3335069,
    lng: 139.4441281,
    season: "8月上旬",
    shellInfo: "大玉も打ち上げられる大規模大会",
    estimatedAltitude: 250,
  },
  {
    id: "katsushika",
    name: "葛飾納涼花火大会",
    prefecture: "東京都",
    venueQuery: "柴又七丁目 東京都葛飾区",
    lat: 35.7592870,
    lng: 139.8796580,
    season: "7月下旬",
    shellInfo: "江戸川河川敷、尺玉を含む大規模花火（約1万5千発）",
    estimatedAltitude: 250,
  },
  {
    id: "tachikawa",
    name: "立川まつり国営昭和記念公園花火大会",
    prefecture: "東京都",
    venueQuery: "国営昭和記念公園 東京都立川市",
    lat: 35.7122591,
    lng: 139.3943216,
    season: "7月下旬",
    shellInfo: "公園内から打ち上げるスターマイン中心の都市型花火",
    estimatedAltitude: 150,
  },
  {
    id: "sagamiko",
    name: "さがみ湖湖上祭花火大会",
    prefecture: "神奈川県",
    venueQuery: "相模湖 神奈川県相模原市緑区",
    lat: 35.6173978,
    lng: 139.1884919,
    season: "8月上旬",
    shellInfo: "湖上花火、水中スターマイン演出あり",
    estimatedAltitude: 200,
  },
  {
    id: "odawara",
    name: "小田原酒匂川花火大会",
    prefecture: "神奈川県",
    venueQuery: "小田原大橋 神奈川県小田原市",
    lat: 35.2679756,
    lng: 139.1739666,
    season: "8月上旬",
    shellInfo: "尺玉を含む約1万発の大規模花火",
    estimatedAltitude: 300,
  },
  {
    id: "enoshima",
    name: "ふじさわ江の島花火大会",
    prefecture: "神奈川県",
    venueQuery: "片瀬海岸 神奈川県藤沢市",
    lat: 35.3107494,
    lng: 139.4818431,
    season: "10月中旬（秋開催）",
    shellInfo: "江の島を背景にした海上花火",
    estimatedAltitude: 180,
  },
  {
    id: "kumagaya",
    name: "熊谷花火大会",
    prefecture: "埼玉県",
    venueQuery: "荒川大橋 埼玉県熊谷市",
    lat: 36.1338149,
    lng: 139.3784298,
    season: "8月上旬",
    shellInfo: "荒川河畔、約1万発",
    estimatedAltitude: 250,
  },
  {
    id: "kounosu",
    name: "こうのす花火大会",
    prefecture: "埼玉県",
    venueQuery: "糠田運動場 埼玉県鴻巣市",
    lat: 36.0623909,
    lng: 139.4854047,
    season: "10月上旬",
    shellInfo: "尺玉連発が中心。フィナーレでは4尺玉(日本最大級)も打ち上げられる",
    estimatedAltitude: 300,
    note: "4尺玉は打ち上げ高さ約750mに達するため、通常より遠方からでも見える可能性がある。号数スライダーで40号に切り替えると目安を確認できる。",
  },
  {
    id: "kawaguchi",
    name: "川口花火大会",
    prefecture: "埼玉県",
    venueQuery: "新荒川大橋 川口市",
    lat: 35.7898927,
    lng: 139.7240858,
    season: "11月中旬（秋開催）",
    shellInfo: "約1万1千発",
    estimatedAltitude: 220,
  },
  {
    id: "todabashi",
    name: "戸田橋花火大会",
    prefecture: "埼玉県",
    venueQuery: "戸田橋 埼玉県戸田市",
    lat: 35.7987551,
    lng: 139.6847706,
    season: "8月上旬",
    shellInfo: "対岸のいたばし花火大会(板橋区)と同時開催、荒川両岸から観覧できる",
    estimatedAltitude: 220,
  },
  {
    id: "soka",
    name: "草加市民納涼大花火大会",
    prefecture: "埼玉県",
    venueQuery: "そうか公園 埼玉県草加市",
    lat: 35.8617036,
    lng: 139.8270101,
    season: "8月上旬",
    shellInfo: "中型玉中心",
    estimatedAltitude: 200,
  },
  {
    id: "ichikawa",
    name: "市川市民納涼花火大会",
    prefecture: "千葉県",
    venueQuery: "大洲防災公園 千葉県市川市",
    lat: 35.7185494,
    lng: 139.9105952,
    season: "8月上旬",
    shellInfo: "対岸の東京都江戸川区花火大会と同時開催、江戸川両岸から観覧できる",
    estimatedAltitude: 220,
  },
  {
    id: "teganuma",
    name: "手賀沼花火大会",
    prefecture: "千葉県",
    venueQuery: "手賀沼公園 千葉県我孫子市",
    lat: 35.8654984,
    lng: 140.0142915,
    season: "8月上旬",
    shellInfo: "手賀沼の両岸(我孫子市・柏市)から観覧できる湖上花火",
    estimatedAltitude: 180,
  },
  {
    id: "kisarazu",
    name: "木更津港まつり花火大会",
    prefecture: "千葉県",
    venueQuery: "中の島大橋 千葉県木更津市",
    lat: 35.3837952,
    lng: 139.9125324,
    season: "8月中旬",
    shellInfo: "東京湾に面した港湾部の花火",
    estimatedAltitude: 200,
  },
  {
    id: "hitachinaka",
    name: "ひたちなか祭り花火大会",
    prefecture: "茨城県",
    venueQuery: "陸上自衛隊勝田駐屯地",
    lat: 36.3820149,
    lng: 140.5300063,
    season: "8月下旬",
    shellInfo: "自衛隊駐屯地内から打ち上げる北関東最大級の花火",
    estimatedAltitude: 250,
  },
  {
    id: "maebashi",
    name: "前橋花火大会",
    prefecture: "群馬県",
    venueQuery: "大渡橋 群馬県前橋市",
    lat: 36.4065242,
    lng: 139.0499654,
    season: "8月上旬",
    shellInfo: "利根川河畔、約1万5千発",
    estimatedAltitude: 250,
  },
  {
    id: "takasaki",
    name: "高崎まつり大花火大会",
    prefecture: "群馬県",
    venueQuery: "和田橋 群馬県高崎市",
    lat: 36.3230556,
    lng: 138.9972318,
    season: "8月下旬",
    shellInfo: "北関東最大級、約1万5千発",
    estimatedAltitude: 280,
  },
  {
    id: "omagari",
    name: "大曲の花火（全国花火競技大会）",
    prefecture: "秋田県",
    venueQuery: "雄物川河川敷 秋田県大仙市",
    lat: 39.5166324,
    lng: 140.5044015,
    season: "8月最終土曜",
    shellInfo: "全国の花火師が技を競う競技大会。10号玉(尺玉)中心、日本三大花火大会の一つ",
    estimatedAltitude: 330,
  },
  {
    id: "nagaoka",
    name: "長岡まつり大花火大会",
    prefecture: "新潟県",
    venueQuery: "長生橋 新潟県長岡市",
    lat: 37.4444305,
    lng: 138.8332798,
    season: "8月上旬(2日間)",
    shellInfo: "正三尺玉(打ち上げ高さ約600m)を含む、日本三大花火大会の一つ",
    estimatedAltitude: 600,
  },
  {
    id: "suwako",
    name: "諏訪湖祭湖上花火大会",
    prefecture: "長野県",
    venueQuery: "諏訪湖 湖畔公園 長野県諏訪市",
    lat: 36.0479452,
    lng: 138.0832846,
    season: "8月中旬",
    shellInfo: "湖上スターマインで知られる全国有数の規模",
    estimatedAltitude: 220,
  },
  {
    id: "suwako_shinsaku",
    name: "全国新作花火チャレンジカップ",
    prefecture: "長野県",
    venueQuery: "諏訪湖 湖畔公園 長野県諏訪市",
    lat: 36.0479452,
    lng: 138.0832846,
    season: "9月の毎週土曜(予選)〜10月末(決勝)",
    shellInfo: "全国の煙火師が競う創作花火。1組につき7号玉1発・4号玉5発・3号玉5発で構成",
    estimatedAltitude: 250,
    note: "「諏訪湖祭湖上花火大会」(suwako)とは同じ諏訪湖畔が舞台の別大会・別日程。",
  },
  {
    id: "toyako",
    name: "洞爺湖ロングラン花火大会",
    prefecture: "北海道",
    venueQuery: "洞爺湖温泉 北海道虻田郡洞爺湖町",
    lat: 42.5635880,
    lng: 140.8177950,
    season: "4月下旬〜10月(毎晩開催)",
    shellInfo: "湖上を移動する台船から打ち上げる、日本一開催日数が多い花火大会",
    estimatedAltitude: 150,
  },
  {
    id: "sendaiTanabata",
    name: "仙台七夕花火祭",
    prefecture: "宮城県",
    venueQuery: "広瀬川河川敷 宮城県仙台市",
    lat: 38.2239545,
    lng: 140.9068682,
    season: "8月上旬",
    shellInfo: "仙台七夕まつり前夜に行われる東北有数の規模、約1万6千発",
    estimatedAltitude: 220,
  },
  {
    id: "yodogawa",
    name: "なにわ淀川花火大会",
    prefecture: "大阪府",
    venueQuery: "新淀川大橋 大阪府大阪市",
    lat: 34.7199957,
    lng: 135.4994698,
    season: "10月中旬(秋開催)",
    shellInfo: "大阪を代表する都市型花火",
    estimatedAltitude: 220,
  },
  {
    id: "biwako",
    name: "びわ湖大花火大会",
    prefecture: "滋賀県",
    venueQuery: "大津港 滋賀県大津市",
    lat: 35.0130753,
    lng: 135.8671302,
    season: "8月上旬",
    shellInfo: "尺玉の斜め打ちで知られる関西有数の規模、約1万2千発",
    estimatedAltitude: 300,
  },
  {
    id: "okazaki",
    name: "岡崎城下家康公夏まつり花火大会",
    prefecture: "愛知県",
    venueQuery: "乙川 愛知県岡崎市",
    lat: 34.9537862,
    lng: 137.1678403,
    season: "8月上旬",
    shellInfo: "市街地中心部での東海地方有数の都市型花火",
    estimatedAltitude: 220,
  },
  {
    id: "atami",
    name: "熱海海上花火大会",
    prefecture: "静岡県",
    venueQuery: "熱海サンビーチ 静岡県熱海市",
    lat: 35.0982401,
    lng: 139.0768609,
    season: "年間十数回開催(夏季は7〜8月中心)",
    shellInfo: "熱海湾の地形を活かした音響効果が特徴。1回あたり約4000発",
    estimatedAltitude: 180,
    note: "年間十数回開催されるため、時期はおおよその目安。詳細日程は公式サイトで確認してください。",
  },
  {
    id: "kobe",
    name: "みなとHANABI-神戸を彩る5日間-",
    prefecture: "兵庫県",
    venueQuery: "メリケンパーク 兵庫県神戸市",
    lat: 34.6833086,
    lng: 135.1893213,
    season: "10月中旬(5日間開催)",
    shellInfo: "神戸港を舞台にした都市型花火",
    estimatedAltitude: 200,
  },
  {
    id: "kurume",
    name: "久留米花火大会",
    prefecture: "福岡県",
    venueQuery: "筑後川 福岡県久留米市",
    lat: 33.3191526,
    lng: 130.4949966,
    season: "8月上旬",
    shellInfo: "西日本最大級の規模",
    estimatedAltitude: 250,
  },
  {
    id: "nagasaki",
    name: "ながさきみなとまつり",
    prefecture: "長崎県",
    venueQuery: "長崎港 長崎県長崎市",
    lat: 32.7073747,
    lng: 129.8232132,
    season: "7月下旬(2日間)",
    shellInfo: "長崎港の夜景と一体になった花火",
    estimatedAltitude: 200,
  },
  {
    id: "ezuko",
    name: "TKU江津湖花火大会",
    prefecture: "熊本県",
    venueQuery: "下江津湖 熊本県熊本市",
    lat: 32.7695600,
    lng: 130.7525076,
    season: "8月下旬",
    shellInfo: "熊本市内最大級、約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "kinkowan",
    name: "かごしま錦江湾サマーナイト大花火大会",
    prefecture: "鹿児島県",
    venueQuery: "鹿児島港本港区 鹿児島県鹿児島市",
    lat: 31.5949375,
    lng: 130.5629999,
    season: "8月下旬",
    shellInfo: "桜島を背景にした南九州最大級の花火",
    estimatedAltitude: 220,
  },
  {
    id: "nagaragawa",
    name: "ぎふ長良川花火大会",
    prefecture: "岐阜県",
    venueQuery: "長良橋 岐阜県岐阜市",
    lat: 35.4395078,
    lng: 136.7734225,
    season: "8月上旬",
    shellInfo: "長良川の鵜飼で知られる川沿いの大規模花火",
    estimatedAltitude: 220,
  },
  {
    id: "nago",
    name: "名護夏まつり",
    prefecture: "沖縄県",
    venueQuery: "名護漁港 沖縄県名護市",
    lat: 26.5870461,
    lng: 127.9816396,
    season: "7月下旬(2日間)",
    shellInfo: "沖縄本島北部最大級の夏祭りのフィナーレ花火",
    estimatedAltitude: 150,
  },
  {
    id: "takamatsu",
    name: "さぬき高松まつり花火大会",
    prefecture: "香川県",
    venueQuery: "サンポート高松 香川県高松市",
    lat: 34.3544360,
    lng: 134.0463950,
    season: "8月中旬",
    shellInfo: "四国最大級、海上から短時間に約6000発",
    estimatedAltitude: 200,
  },
  {
    id: "hiroshimaMinato",
    name: "広島みなと夢花火大会",
    prefecture: "広島県",
    venueQuery: "宇品海岸 広島県広島市",
    lat: 34.3542762,
    lng: 132.4552761,
    season: "7月下旬",
    shellInfo: "10号玉を含む瀬戸内海を舞台にした約1万発",
    estimatedAltitude: 280,
  },
  {
    id: "kawaguchiko",
    name: "河口湖湖上祭",
    prefecture: "山梨県",
    venueQuery: "八木崎公園 山梨県富士河口湖町",
    lat: 35.5121059,
    lng: 138.7558579,
    season: "8月上旬",
    shellInfo: "富士山を背景にしたミュージック花火、県内最大級",
    estimatedAltitude: 250,
  },
  {
    id: "kuwana",
    name: "桑名水郷花火大会",
    prefecture: "三重県",
    venueQuery: "揖斐川 三重県桑名市",
    lat: 35.0290960,
    lng: 136.7174025,
    season: "7月下旬",
    shellInfo: "揖斐川の中洲から打ち上げる約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "otaru",
    name: "小樽潮まつり（花火大会）",
    prefecture: "北海道",
    venueQuery: "小樽港 北海道小樽市",
    lat: 43.1990757,
    lng: 141.0160875,
    season: "7月下旬",
    shellInfo: "小樽港まつりのフィナーレを飾る、道内屈指の人気花火",
    estimatedAltitude: 180,
  },
  {
    id: "obihiro",
    name: "勝毎花火大会",
    prefecture: "北海道",
    venueQuery: "帯広市 北海道",
    lat: 42.9238090,
    lng: 143.1966324,
    season: "8月13日",
    shellInfo: "十勝地方最大級の花火大会",
    estimatedAltitude: 200,
  },
  {
    id: "aomoriHanabi",
    name: "青森花火大会（ねぶた祭協賛）",
    prefecture: "青森県",
    venueQuery: "青森港 青森県青森市",
    lat: 40.7336520,
    lng: 140.6883574,
    season: "8月7日（ねぶた祭最終日）",
    shellInfo: "ねぶた祭のフィナーレ、超巨大スターマインや二尺玉",
    estimatedAltitude: 450,
  },
  {
    id: "towadako",
    name: "十和田湖湖水まつり",
    prefecture: "青森県",
    venueQuery: "十和田湖 青森県",
    lat: 40.4586552,
    lng: 140.8711305,
    season: "7月中旬",
    shellInfo: "10号玉(尺玉)あり、音楽と花火のコラボレーション",
    estimatedAltitude: 300,
  },
  {
    id: "ichinoseki",
    name: "かわさき夏まつり花火大会",
    prefecture: "岩手県",
    venueQuery: "一関市 岩手県",
    lat: 38.9346961,
    lng: 141.1266050,
    season: "8月中旬",
    shellInfo: "県内最大級の二尺玉、水中花火が特徴",
    estimatedAltitude: 450,
  },
  {
    id: "morioka",
    name: "盛岡花火の祭典",
    prefecture: "岩手県",
    venueQuery: "都南大橋 岩手県盛岡市",
    lat: 39.6597001,
    lng: 141.1725018,
    season: "8月中旬",
    shellInfo: "全国の花火師が競演、連発花火・仕掛け花火",
    estimatedAltitude: 200,
  },
  {
    id: "shiogama",
    name: "塩竈みなと祭前夜祭花火大会",
    prefecture: "宮城県",
    venueQuery: "塩竈港 宮城県塩竈市",
    lat: 38.3117175,
    lng: 141.0349432,
    season: "7月（海の日前後）",
    shellInfo: "海上安全祈願、約6000発",
    estimatedAltitude: 180,
  },
  {
    id: "natori",
    name: "なとり夏まつり",
    prefecture: "宮城県",
    venueQuery: "閖上 宮城県名取市",
    lat: 38.1752405,
    lng: 140.9430021,
    season: "8月上旬",
    shellInfo: "約1万発",
    estimatedAltitude: 200,
  },
  {
    id: "noshiro",
    name: "能代の花火",
    prefecture: "秋田県",
    venueQuery: "米代川 秋田県能代市",
    lat: 40.1965056,
    lng: 140.0418797,
    season: "8月",
    shellInfo: "約1万5千発の大規模花火",
    estimatedAltitude: 250,
  },
  {
    id: "akagawa",
    name: "赤川花火大会",
    prefecture: "山形県",
    venueQuery: "鶴岡市 山形県",
    lat: 38.7272187,
    lng: 139.8266292,
    season: "8月中旬",
    shellInfo: "「感動日本一」、音楽と花火が高度にシンクロ、約1万2千発",
    estimatedAltitude: 250,
  },
  {
    id: "yamagataHanabi",
    name: "山形大花火大会",
    prefecture: "山形県",
    venueQuery: "霞城公園 山形県山形市",
    lat: 38.2555771,
    lng: 140.3282413,
    season: "8月中旬",
    shellInfo: "県内有数のイベント、約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "fukushimaHanabi",
    name: "ふくしま花火大会",
    prefecture: "福島県",
    venueQuery: "信夫ケ丘競技場 福島市",
    lat: 37.7752462,
    lng: 140.4906601,
    season: "7月下旬",
    shellInfo: "最大1尺玉の大玉花火、音楽花火。約8000発",
    estimatedAltitude: 300,
    note: "会場は信夫ヶ丘緑地(阿武隈川と松川の合流点)。近傍の信夫ケ丘競技場を基準地点にしている。",
  },
  {
    id: "koriyama",
    name: "あさか野夏まつり花火大会",
    prefecture: "福島県",
    venueQuery: "安積永盛駅 郡山市",
    lat: 37.3570454,
    lng: 140.3691401,
    season: "8月14日",
    shellInfo: "尺玉・スターマイン・音楽花火など約7000発。田園地帯での打ち上げのため360度どこからでも観覧可能",
    estimatedAltitude: 300,
    note: "会場(AGCエレクトロニクス郡山カルチャーパーク)最寄りの安積永盛駅を基準地点にしている(会場まで徒歩約15分)。",
  },
  {
    id: "niigataMatsuri",
    name: "新潟まつり花火大会",
    prefecture: "新潟県",
    venueQuery: "信濃川 新潟県新潟市中央区",
    lat: 37.8968411,
    lng: 139.0128671,
    season: "8月上旬",
    shellInfo: "ワイドスターマインなど、信濃川を舞台にした花火",
    estimatedAltitude: 200,
  },
  {
    id: "toyamaMatsuri",
    name: "北日本新聞納涼花火",
    prefecture: "富山県",
    venueQuery: "富山市 富山県",
    lat: 36.6957569,
    lng: 137.2136215,
    season: "8月上旬",
    shellInfo: "尺玉の連発を含む約3000発",
    estimatedAltitude: 300,
  },
  {
    id: "nanao",
    name: "北國花火七尾大会",
    prefecture: "石川県",
    venueQuery: "七尾市 石川県",
    lat: 37.0521078,
    lng: 136.9464610,
    season: "7月中旬",
    shellInfo: "七尾湾に映る大輪の花火",
    estimatedAltitude: 200,
  },
  {
    id: "wakura",
    name: "和倉温泉中日夏花火大会",
    prefecture: "石川県",
    venueQuery: "和倉温泉",
    lat: 37.0749120,
    lng: 136.9256945,
    season: "8月上旬",
    shellInfo: "北陸最大級の三尺玉、直径約600mの大輪",
    estimatedAltitude: 600,
  },
  {
    id: "mikuni",
    name: "三国花火",
    prefecture: "福井県",
    venueQuery: "坂井市 福井県",
    lat: 36.1677146,
    lng: 136.2262832,
    season: "8月中旬",
    shellInfo: "水中二尺玉が名物、二尺玉・尺玉を含む約1万5千発",
    estimatedAltitude: 450,
  },
  {
    id: "katsuyama",
    name: "勝山納涼花火大会",
    prefecture: "福井県",
    venueQuery: "勝山駅 福井県勝山市",
    lat: 36.0562881,
    lng: 136.4921217,
    season: "8月中旬",
    shellInfo: "約3000発。会場は弁天緑地公園",
    estimatedAltitude: 200,
  },
  {
    id: "shinmei",
    name: "市川三郷ふるさと夏まつり 神明の花火大会",
    prefecture: "山梨県",
    venueQuery: "三郡西橋 市川三郷町",
    lat: 35.5668376,
    lng: 138.4892368,
    season: "8月7日",
    shellInfo: "県内最大級、約2万発",
    estimatedAltitude: 220,
  },
  {
    id: "chikuma",
    name: "千曲川納涼煙火大会",
    prefecture: "長野県",
    venueQuery: "千曲川 長野県千曲市戸倉上山田温泉",
    lat: 36.4790191,
    lng: 138.1458704,
    season: "8月中旬",
    shellInfo: "超特大スターマイン、約300mのナイアガラ、約1万発",
    estimatedAltitude: 250,
  },
  {
    id: "ebisukou",
    name: "長野えびす講煙火大会",
    prefecture: "長野県",
    venueQuery: "長野市 長野県",
    lat: 36.6485851,
    lng: 138.1947664,
    season: "11月下旬（晩秋開催）",
    shellInfo: "全国的にも珍しい晩秋開催、特大尺玉、ミュージックスターマイン",
    estimatedAltitude: 300,
  },
  {
    id: "fukuroi",
    name: "全国花火名人選抜競技大会 ふくろい遠州の花火",
    prefecture: "静岡県",
    venueQuery: "袋井市 静岡県",
    lat: 34.7501492,
    lng: 137.9246962,
    season: "5月（競技大会）",
    shellInfo: "全国から集まった花火師が8号玉などで技を競う",
    estimatedAltitude: 280,
  },
  {
    id: "numazu",
    name: "沼津夏まつり・狩野川花火大会",
    prefecture: "静岡県",
    venueQuery: "狩野川 静岡県沼津市",
    lat: 35.0455027,
    lng: 138.9201903,
    season: "7月下旬",
    shellInfo: "市街地花火として東海地方随一の規模",
    estimatedAltitude: 220,
  },
  {
    id: "toyota",
    name: "とよたおいでん祭 花火大会",
    prefecture: "愛知県",
    venueQuery: "矢作川 愛知県豊田市",
    lat: 35.2613473,
    lng: 137.4976189,
    season: "7月下旬",
    shellInfo: "著名花火師の創作花火、手筒花火、音楽花火",
    estimatedAltitude: 220,
  },
  {
    id: "nagoyaPort",
    name: "名古屋みなと祭 水上花火大会",
    prefecture: "愛知県",
    venueQuery: "名古屋港 愛知県名古屋市",
    lat: 35.0930989,
    lng: 136.8819049,
    season: "7月",
    shellInfo: "音楽と精密にシンクロ、約1万3千発",
    estimatedAltitude: 220,
  },
  {
    id: "kumanoDaihanabi",
    name: "熊野大花火大会",
    prefecture: "三重県",
    venueQuery: "七里御浜 三重県熊野市",
    lat: 33.8209125,
    lng: 136.0540142,
    season: "8月17日",
    shellInfo: "三尺玉海上自爆で知られる、直径約600m、約1万発",
    estimatedAltitude: 600,
  },
  {
    id: "hozugawa",
    name: "京都・保津川花火大会",
    prefecture: "京都府",
    venueQuery: "保津川 京都府亀岡市",
    lat: 35.0174310,
    lng: 135.5866400,
    season: "8月",
    shellInfo: "過去最大規模、約1万3千発",
    estimatedAltitude: 220,
  },
  {
    id: "miyazu",
    name: "宮津燈籠流し花火大会",
    prefecture: "京都府",
    venueQuery: "宮津湾 京都府宮津市",
    lat: 35.5716280,
    lng: 135.2065222,
    season: "8月",
    shellInfo: "日本三大燈籠流しの一つ、約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "inagawa",
    name: "猪名川花火大会",
    prefecture: "大阪府",
    venueQuery: "猪名川 大阪府池田市",
    lat: 34.8156520,
    lng: 135.4172091,
    season: "8月",
    shellInfo: "1948年から続く伝統、両岸合わせて10万人超が来場",
    estimatedAltitude: 200,
  },
  {
    id: "awaji",
    name: "淡路島まつり花火大会",
    prefecture: "兵庫県",
    venueQuery: "洲本市 兵庫県",
    lat: 34.3429508,
    lng: 134.8954223,
    season: "8月",
    shellInfo: "兵庫県下最大級、直径約320mの大玉",
    estimatedAltitude: 250,
  },
  {
    id: "ashiya",
    name: "芦屋サマーカーニバル",
    prefecture: "兵庫県",
    venueQuery: "芦屋市 兵庫県",
    lat: 34.7273701,
    lng: 135.3040278,
    season: "8月上旬",
    shellInfo: "音楽と3Dパノラマ花火のシンクロ",
    estimatedAltitude: 180,
  },
  {
    id: "narasugoshi",
    name: "おんぱら祭奉納花火大会",
    prefecture: "奈良県",
    venueQuery: "大神神社 桜井市",
    lat: 34.5287534,
    lng: 135.8517574,
    season: "7月31日",
    shellInfo: "最大5号、約2000発",
    estimatedAltitude: 150,
  },
  {
    id: "ikomaDondoko",
    name: "いこまどんどこまつり納涼花火大会",
    prefecture: "奈良県",
    venueQuery: "高山竹林園 生駒市",
    lat: 34.7501668,
    lng: 135.7210898,
    season: "8月上旬",
    shellInfo: "高山竹林園・むかいやま公園の2か所から打ち上げ",
    estimatedAltitude: 150,
  },
  {
    id: "udaHaibara",
    name: "宇陀市はいばら花火大会",
    prefecture: "奈良県",
    venueQuery: "榛原駅 宇陀市",
    lat: 34.5298948,
    lng: 135.9546646,
    season: "8月上旬",
    shellInfo: "全国花火まほろば競技会。複数の小学校グラウンドから打ち上げ",
    estimatedAltitude: 200,
  },
  {
    id: "shingu",
    name: "熊野徐福万燈祭（新宮花火大会）",
    prefecture: "和歌山県",
    venueQuery: "熊野川 和歌山県新宮市",
    lat: 33.8083254,
    lng: 135.8720917,
    season: "8月中旬",
    shellInfo: "供養祭に合わせた花火、約6000発",
    estimatedAltitude: 200,
  },
  {
    id: "tottoriShanshan",
    name: "鳥取しゃんしゃん祭 市民納涼花火大会",
    prefecture: "鳥取県",
    venueQuery: "千代川 鳥取県鳥取市",
    lat: 35.5361923,
    lng: 134.1926609,
    season: "8月中旬（お盆）",
    shellInfo: "市民納涼花火",
    estimatedAltitude: 200,
  },
  {
    id: "yonago",
    name: "米子がいな祭 大花火大会",
    prefecture: "鳥取県",
    venueQuery: "米子市 鳥取県",
    lat: 35.4276408,
    lng: 133.3314590,
    season: "8月上旬",
    shellInfo: "大規模花火",
    estimatedAltitude: 200,
  },
  {
    id: "matsueSuigosai",
    name: "松江水郷祭湖上花火大会",
    prefecture: "島根県",
    venueQuery: "宍道湖 島根県松江市",
    lat: 35.4436453,
    lng: 132.9603315,
    season: "8月上旬（2日間）",
    shellInfo: "2日間合計約2万1千発、宍道湖上の花火",
    estimatedAltitude: 250,
  },
  {
    id: "tamano",
    name: "玉野まつり花火大会",
    prefecture: "岡山県",
    venueQuery: "宇野港 岡山県玉野市",
    lat: 34.5104102,
    lng: 133.9659377,
    season: "8月上旬",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "kanmon",
    name: "関門海峡花火大会",
    prefecture: "山口県",
    venueQuery: "関門海峡 山口県下関市",
    lat: 33.9218085,
    lng: 130.9318982,
    season: "8月13日",
    shellInfo: "福岡・門司と山口・下関の両岸から、合計約1万8千発",
    estimatedAltitude: 220,
  },
  {
    id: "nishiawa",
    name: "にし阿波の花火",
    prefecture: "徳島県",
    venueQuery: "三好市 徳島県",
    lat: 34.0259698,
    lng: 133.8071758,
    season: "11月中旬（秋開催）",
    shellInfo: "西日本最大級、約2万発、音楽と完全にシンクロ",
    estimatedAltitude: 250,
  },
  {
    id: "matsuyamaMinato",
    name: "松山港まつり・三津浜花火大会",
    prefecture: "愛媛県",
    venueQuery: "三津浜 愛媛県松山市",
    lat: 33.8624681,
    lng: 132.7287491,
    season: "8月上旬",
    shellInfo: "愛媛県下最大級、1万発超の海上花火",
    estimatedAltitude: 220,
  },
  {
    id: "kochiHanabi",
    name: "高知市納涼花火大会",
    prefecture: "高知県",
    venueQuery: "高知市 高知県",
    lat: 33.5588482,
    lng: 133.5310918,
    season: "8月上旬（よさこい祭り前）",
    shellInfo: "西日本有数の市民花火大会、約4000発",
    estimatedAltitude: 200,
  },
  {
    id: "susaki",
    name: "須崎まつり海上花火大会",
    prefecture: "高知県",
    venueQuery: "須崎市 高知県",
    lat: 33.4008260,
    lng: 133.2829594,
    season: "8月上旬",
    shellInfo: "2尺玉が夜空に映える海上花火",
    estimatedAltitude: 450,
  },
  {
    id: "kukinoumi",
    name: "くきのうみ花火の祭典",
    prefecture: "福岡県",
    venueQuery: "若戸大橋 福岡県北九州市",
    lat: 33.9027905,
    lng: 130.8166413,
    season: "8月",
    shellInfo: "台船上からの大玉7号を含む海上花火",
    estimatedAltitude: 250,
  },
  {
    id: "karatsuHanabi",
    name: "唐津花火大会",
    prefecture: "佐賀県",
    venueQuery: "松浦川 佐賀県唐津市",
    lat: 33.4462660,
    lng: 129.8611630,
    season: "10月上旬（秋開催）",
    shellInfo: "3尺玉ナイアガラ花火が名物、音楽とシンクロ",
    estimatedAltitude: 600,
    note: "4尺玉も打ち上げられることがある。号数スライダーで40号に切り替えると目安を確認できる。",
  },
  {
    id: "oitaYume",
    name: "おおいた「夢」花火",
    prefecture: "大分県",
    venueQuery: "大分市 大分県",
    lat: 33.2393864,
    lng: 131.6096524,
    season: "8月29日",
    shellInfo: "約8000発",
    estimatedAltitude: 220,
  },
  {
    id: "nobeoka",
    name: "まつりのべおか花火大会",
    prefecture: "宮崎県",
    venueQuery: "延岡市 宮崎県",
    lat: 32.5823063,
    lng: 131.6649034,
    season: "7月中旬",
    shellInfo: "約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "makurazaki",
    name: "枕崎港まつり花火大会",
    prefecture: "鹿児島県",
    venueQuery: "枕崎港 鹿児島県枕崎市",
    lat: 31.2688253,
    lng: 130.2944752,
    season: "8月",
    shellInfo: "南薩最大の夏祭り、九州唯一の3尺玉",
    estimatedAltitude: 600,
  },
  {
    id: "churaumi",
    name: "海洋博美ら海花火大会",
    prefecture: "沖縄県",
    venueQuery: "海洋博公園 沖縄県本部町",
    lat: 26.6896980,
    lng: 127.8766766,
    season: "不定期（年数回開催）",
    shellInfo: "約1万発、音楽に合わせて打ち上げ",
    estimatedAltitude: 200,
    note: "開催日が年数回に分かれるため、詳細日程は公式サイトで確認してください。",
  },
  {
    id: "ishinomaki",
    name: "石巻川開き祭り花火大会",
    prefecture: "宮城県",
    venueQuery: "中瀬公園 宮城県石巻市",
    lat: 38.4287975,
    lng: 141.3106671,
    season: "8月上旬",
    shellInfo: "100回を超える歴史ある祭り、北上川河畔の花火",
    estimatedAltitude: 200,
  },
  {
    id: "yaizu",
    name: "焼津海上花火大会",
    prefecture: "静岡県",
    venueQuery: "焼津漁港 静岡県焼津市",
    lat: 34.8691617,
    lng: 138.3262943,
    season: "10月（秋開催）",
    shellInfo: "秋開催で例年約20万人が訪れる人気大会",
    estimatedAltitude: 200,
  },
  {
    id: "nishio",
    name: "西尾の夏まつり花火大会",
    prefecture: "愛知県",
    venueQuery: "矢作古川 愛知県西尾市",
    lat: 34.8396464,
    lng: 137.0688092,
    season: "8月中旬",
    shellInfo: "灯籠流しと花火、矢作古川沿いの夏まつり",
    estimatedAltitude: 180,
  },
  {
    id: "itami",
    name: "いたみ花火大会",
    prefecture: "兵庫県",
    venueQuery: "猪名川 兵庫県伊丹市",
    lat: 34.8007476,
    lng: 135.4248758,
    season: "10月（秋開催）",
    shellInfo: "「黄金しだれ柳の重ね咲き」で知られる歴史ある花火大会",
    estimatedAltitude: 200,
  },
  {
    id: "kasumi",
    name: "香住ふるさとまつり海上花火大会",
    prefecture: "兵庫県",
    venueQuery: "香住 兵庫県美方郡香美町",
    lat: 35.6355358,
    lng: 134.6231469,
    season: "8月",
    shellInfo: "兵庫県4大花火大会の一つ、スターマイン・尺玉・斜め打ち",
    estimatedAltitude: 300,
  },
  {
    id: "haruna",
    name: "榛名湖花火大会",
    prefecture: "群馬県",
    venueQuery: "榛名湖 群馬県高崎市",
    lat: 36.4752798,
    lng: 138.8659063,
    season: "8月",
    shellInfo: "榛名湖畔の花火大会",
    estimatedAltitude: 180,
  },
  {
    id: "kiryu",
    name: "桐生花火大会",
    prefecture: "群馬県",
    venueQuery: "小梅琴平公園 桐生市",
    lat: 36.3939996,
    lng: 139.3376744,
    season: "9月下旬",
    shellInfo: "約3000発。渡良瀬川沿い、民間主体の運営",
    estimatedAltitude: 200,
  },
  {
    id: "tousuibashi",
    name: "刀水橋花火大会",
    prefecture: "群馬県",
    venueQuery: "刀水橋 太田市",
    lat: 36.2390644,
    lng: 139.3781137,
    season: "10月下旬〜11月",
    shellInfo: "約1万発。利根川河畔で群馬県太田市・大泉町と埼玉県熊谷市が共同開催",
    estimatedAltitude: 300,
  },
  {
    id: "moka",
    name: "真岡市夏祭大花火大会",
    prefecture: "栃木県",
    venueQuery: "五行川 栃木県真岡市",
    lat: 36.4182163,
    lng: 139.9931613,
    season: "8月",
    shellInfo: "五行川を舞台にした人気の花火大会",
    estimatedAltitude: 200,
  },
  {
    id: "utsunomiya",
    name: "うつのみや花火大会",
    prefecture: "栃木県",
    venueQuery: "道場宿緑地 宇都宮市",
    lat: 36.5630757,
    lng: 139.9701716,
    season: "8月上旬",
    shellInfo: "約2万発。鬼怒川河川敷(道場宿緑地)で開催",
    estimatedAltitude: 300,
  },
  {
    id: "kanuma",
    name: "鹿沼さつき祭り花火大会",
    prefecture: "栃木県",
    venueQuery: "黒川 栃木県鹿沼市",
    lat: 36.5642131,
    lng: 139.7547408,
    season: "5月下旬",
    shellInfo: "黒川を舞台にした本格的な花火",
    estimatedAltitude: 180,
  },
  {
    id: "sendaigawa",
    name: "川内川花火大会",
    prefecture: "鹿児島県",
    venueQuery: "川内川 鹿児島県薩摩川内市",
    lat: 31.8133528,
    lng: 130.3121704,
    season: "8月",
    shellInfo: "約1000mの巨大ナイアガラ、大玉(8号)を含む",
    estimatedAltitude: 280,
  },
  {
    id: "aira",
    name: "あいら市花火大会",
    prefecture: "鹿児島県",
    venueQuery: "姶良市 鹿児島県",
    lat: 31.7282816,
    lng: 130.6276569,
    season: "8月",
    shellInfo: "水中花火を含む約1万発",
    estimatedAltitude: 200,
  },
  {
    id: "ichikikushikino",
    name: "いちき串木野花火大会",
    prefecture: "鹿児島県",
    venueQuery: "いちき串木野市 鹿児島県",
    lat: 31.7146024,
    lng: 130.2721599,
    season: "7月（祇園祭と同日）",
    shellInfo: "東シナ海を背景にした花火",
    estimatedAltitude: 180,
  },
  {
    id: "kashiihama",
    name: "香椎浜花火大会",
    prefecture: "福岡県",
    venueQuery: "香椎浜 福岡県福岡市東区",
    lat: 33.6567294,
    lng: 130.4336540,
    season: "8月",
    shellInfo: "香椎浜沖からの海上花火",
    estimatedAltitude: 180,
  },
  {
    id: "iizuka",
    name: "飯塚納涼花火大会",
    prefecture: "福岡県",
    venueQuery: "飯塚市 福岡県",
    lat: 33.6465940,
    lng: 130.6911579,
    season: "8月",
    shellInfo: "400mの大型ナイアガラが名物",
    estimatedAltitude: 180,
  },
  {
    id: "shimabara",
    name: "島原温泉ガマダス花火大会",
    prefecture: "長崎県",
    venueQuery: "島原温泉 長崎県島原市",
    lat: 32.7847898,
    lng: 130.3692367,
    season: "8月",
    shellInfo: "島原温泉街の夏の花火",
    estimatedAltitude: 180,
  },
  {
    id: "ashizuri",
    name: "市民祭あしずりまつり花火大会",
    prefecture: "高知県",
    venueQuery: "土佐清水市 高知県",
    lat: 32.7816213,
    lng: 132.9548557,
    season: "8月",
    shellInfo: "県西部を代表する花火大会",
    estimatedAltitude: 180,
  },
  {
    id: "shimanto",
    name: "しまんと市民祭 しまんと納涼花火大会",
    prefecture: "高知県",
    venueQuery: "四万十市 高知県",
    lat: 32.9912232,
    lng: 132.9336984,
    season: "8月",
    shellInfo: "四万十川に映る花火が特徴",
    estimatedAltitude: 180,
  },
  {
    id: "naruto",
    name: "鳴門の夏まつり 歌と花火のファンタジックショー",
    prefecture: "徳島県",
    venueQuery: "鳴門・大塚スポーツパーク 鳴門市",
    lat: 34.1671671,
    lng: 134.6177322,
    season: "8月上旬",
    shellInfo: "約1000発。音楽・レーザーと合わせた花火ショー形式",
    estimatedAltitude: 180,
  },
  {
    id: "kaminaka",
    name: "かみなか納涼祭花火大会",
    prefecture: "徳島県",
    venueQuery: "那賀町 徳島県",
    lat: 33.8574558,
    lng: 134.4967307,
    season: "8月中旬",
    shellInfo: "地域の夏祭り花火",
    estimatedAltitude: 150,
  },
  {
    id: "yoshinogawaCity",
    name: "吉野川市納涼花火大会",
    prefecture: "徳島県",
    venueQuery: "鴨島駅 吉野川市",
    lat: 34.0737051,
    lng: 134.3565300,
    season: "8月上旬",
    shellInfo: "約1000発。吉野川市鴨島運動場で開催",
    estimatedAltitude: 150,
  },
  {
    id: "ayabe",
    name: "あやべ水無月まつり",
    prefecture: "京都府",
    venueQuery: "由良川 京都府綾部市",
    lat: 35.3092282,
    lng: 135.2614317,
    season: "6月",
    shellInfo: "由良川沿いで打ち上げるスターマイン",
    estimatedAltitude: 180,
  },
  {
    id: "maizuru",
    name: "みなと舞鶴ちゃったまつり花火大会",
    prefecture: "京都府",
    venueQuery: "舞鶴港 京都府舞鶴市",
    lat: 35.5661803,
    lng: 135.4265378,
    season: "8月",
    shellInfo: "舞鶴港を舞台にした花火",
    estimatedAltitude: 180,
  },
  {
    id: "isawa",
    name: "石和温泉花火大会",
    prefecture: "山梨県",
    venueQuery: "石和温泉 山梨県笛吹市",
    lat: 35.6576040,
    lng: 138.6353509,
    season: "8月",
    shellInfo: "温泉街の夏の風物詩",
    estimatedAltitude: 180,
  },
  {
    id: "fuefukigawaKenka",
    name: "笛吹川県下納涼花火大会",
    prefecture: "山梨県",
    venueQuery: "山梨市駅 山梨市",
    lat: 35.6849247,
    lng: 138.6830069,
    season: "7月下旬",
    shellInfo: "約5000発。笛吹川万力大橋下流で開催",
    estimatedAltitude: 250,
  },
  {
    id: "kawanakajima",
    name: "川中島古戦場まつり花火大会",
    prefecture: "長野県",
    venueQuery: "川中島古戦場史跡公園 長野県長野市",
    lat: 36.5897750,
    lng: 138.1868360,
    season: "秋",
    shellInfo: "ミュージックスターマインなど約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "ueda",
    name: "信州上田大花火大会",
    prefecture: "長野県",
    venueQuery: "上田市 長野県",
    lat: 36.4021192,
    lng: 138.2490506,
    season: "8月",
    shellInfo: "スターマイン中心の約8000発、音楽シンクロ",
    estimatedAltitude: 200,
  },
  {
    id: "hakodate",
    name: "函館港まつり 道新花火大会",
    prefecture: "北海道",
    venueQuery: "函館港 北海道函館市",
    lat: 41.7698758,
    lng: 140.8250168,
    season: "8月上旬",
    shellInfo: "函館港まつりを彩る花火",
    estimatedAltitude: 200,
  },
  {
    id: "chitose",
    name: "千歳市民花火大会",
    prefecture: "北海道",
    venueQuery: "陸上自衛隊北千歳駐屯地 千歳市",
    lat: 42.8297776,
    lng: 141.6210000,
    season: "7月下旬",
    shellInfo: "約4000発。陸上自衛隊北千歳駐屯地スキー訓練場で開催",
    estimatedAltitude: 250,
  },
  {
    id: "kushiro",
    name: "釧路大漁どんぱく花火大会",
    prefecture: "北海道",
    venueQuery: "幣舞橋 釧路市",
    lat: 42.9821436,
    lng: 144.3851811,
    season: "9月中旬",
    shellInfo: "約1万発。開花直径約600mの三尺玉(道東最大級)。釧路川河口(幣舞橋下流)で開催",
    estimatedAltitude: 600,
  },
  {
    id: "tomakomai",
    name: "とまみん苫小牧百年花火",
    prefecture: "北海道",
    venueQuery: "苫小牧港 苫小牧市",
    lat: 42.6308819,
    lng: 141.6177610,
    season: "10月",
    shellInfo: "苫小牧漁港区西側広場で開催（例年の港まつりとは別日程）",
    estimatedAltitude: 200,
  },
  {
    id: "asahikawa",
    name: "旭川夏まつり 道新納涼花火大会",
    prefecture: "北海道",
    venueQuery: "旭川市 北海道",
    lat: 43.7706250,
    lng: 142.3649743,
    season: "7月〜8月",
    shellInfo: "道北最大級の花火大会",
    estimatedAltitude: 200,
  },
  {
    id: "hachinohe",
    name: "八戸花火大会",
    prefecture: "青森県",
    venueQuery: "八戸市 青森県",
    lat: 40.5122388,
    lng: 141.4882959,
    season: "8月中旬",
    shellInfo: "尺玉を含む約7000発、館鼻漁港を舞台",
    estimatedAltitude: 300,
  },
  {
    id: "goshogawara",
    name: "五所川原花火大会",
    prefecture: "青森県",
    venueQuery: "五所川原市 青森県",
    lat: 40.8076098,
    lng: 140.4459462,
    season: "8月上旬",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "yurihonjo",
    name: "由利本荘市花火大会",
    prefecture: "秋田県",
    venueQuery: "友水公園 秋田県由利本荘市",
    lat: 39.3928720,
    lng: 140.0380079,
    season: "7月下旬",
    shellInfo: "地域の夏の花火大会",
    estimatedAltitude: 180,
  },
  {
    id: "yonezawa",
    name: "米沢納涼水上花火大会",
    prefecture: "山形県",
    venueQuery: "松が岬公園 山形県米沢市",
    lat: 37.9094952,
    lng: 140.1048563,
    season: "8月上旬",
    shellInfo: "堀を舞台にした水上花火、約5000発",
    estimatedAltitude: 180,
  },
  {
    id: "kashiwazaki",
    name: "ぎおん柏崎まつり 海の大花火大会",
    prefecture: "新潟県",
    venueQuery: "柏崎市 新潟県",
    lat: 37.3719095,
    lng: 138.5591406,
    season: "7月下旬",
    shellInfo: "三尺玉海上空中大スターマインで知られる大規模花火",
    estimatedAltitude: 600,
  },
  {
    id: "joetsu",
    name: "上越まつり大花火大会",
    prefecture: "新潟県",
    venueQuery: "直江津 新潟県上越市",
    lat: 37.1702640,
    lng: 138.2422616,
    season: "7月〜8月",
    shellInfo: "直江津地区の花火大会",
    estimatedAltitude: 200,
  },
  {
    id: "iwaki",
    name: "いわき花火大会",
    prefecture: "福島県",
    venueQuery: "アクアマリンふくしま いわき市",
    lat: 36.9425928,
    lng: 140.9010779,
    season: "8月上旬",
    shellInfo: "120分間の音楽創作スターマイン。約1万2000発",
    estimatedAltitude: 300,
    note: "会場はアクアマリンパーク(小名浜港1・2号埠頭間)。近傍のアクアマリンふくしまを基準地点にしている。",
  },
  {
    id: "yanaizuReitamatsuri",
    name: "霊まつり流灯花火大会",
    prefecture: "福島県",
    venueQuery: "会津柳津駅 河沼郡柳津町",
    lat: 37.5390794,
    lng: 139.7238157,
    season: "8月10日",
    shellInfo: "約7000発。只見川に約500基の灯篭を浮かべる伝統行事",
    estimatedAltitude: 250,
  },
  {
    id: "shirakawaSekiMatsuri",
    name: "白河関まつり市民納涼花火大会",
    prefecture: "福島県",
    venueQuery: "小峰城 白河市",
    lat: 37.1327408,
    lng: 140.2138267,
    season: "8月上旬",
    shellInfo: "白河駅前・城山公園(小峰城)周辺で開催",
    estimatedAltitude: 200,
  },
  {
    id: "takayama",
    name: "高山の花火",
    prefecture: "岐阜県",
    venueQuery: "高山市 岐阜県",
    lat: 36.1396246,
    lng: 137.2510322,
    season: "8月上旬",
    shellInfo: "飛騨高山の夏の花火大会",
    estimatedAltitude: 180,
  },
  {
    id: "tajimi",
    name: "多治見市制記念花火大会",
    prefecture: "岐阜県",
    venueQuery: "土岐川 岐阜県多治見市",
    lat: 35.3510768,
    lng: 137.1632168,
    season: "11月（秋開催）",
    shellInfo: "土岐川河川敷の花火",
    estimatedAltitude: 180,
  },
  {
    id: "hikone",
    name: "彦根・北びわ湖納涼花火大会",
    prefecture: "滋賀県",
    venueQuery: "彦根港 滋賀県彦根市",
    lat: 35.2854344,
    lng: 136.2480510,
    season: "8月",
    shellInfo: "彦根城を背景にした花火",
    estimatedAltitude: 200,
  },
  {
    id: "nagahamaJin",
    name: "長浜の陣",
    prefecture: "滋賀県",
    venueQuery: "長浜港 滋賀県長浜市",
    lat: 35.3731339,
    lng: 136.2641139,
    season: "9月",
    shellInfo: "秀吉ゆかりの城下町、3日間連続開催",
    estimatedAltitude: 200,
  },
  {
    id: "tsuyama",
    name: "津山納涼ごんごまつり花火大会",
    prefecture: "岡山県",
    venueQuery: "津山市 岡山県",
    lat: 35.0691284,
    lng: 134.0043355,
    season: "8月上旬",
    shellInfo: "岡山県下最大級、約8300発",
    estimatedAltitude: 250,
  },
  {
    id: "onomichi",
    name: "おのみち住吉花火まつり",
    prefecture: "広島県",
    venueQuery: "尾道水道 広島県尾道市",
    lat: 34.3054364,
    lng: 133.0889854,
    season: "7月下旬",
    shellInfo: "江戸時代中期から続く伝統行事、狭い海峡での花火、約1万3千発",
    estimatedAltitude: 200,
  },
  {
    id: "yamaguchiFushino",
    name: "ふしの夏まつり花火大会",
    prefecture: "山口県",
    venueQuery: "椹野川 山口県山口市",
    lat: 34.1573536,
    lng: 131.4608198,
    season: "7月下旬",
    shellInfo: "約3万5千人が訪れる山口市の花火",
    estimatedAltitude: 180,
  },
  {
    id: "iwakuniMinato",
    name: "岩国港みなと祭花火大会",
    prefecture: "山口県",
    venueQuery: "岩国港 山口県岩国市",
    lat: 34.1904780,
    lng: 132.2340564,
    season: "7月中旬",
    shellInfo: "港まつりの花火",
    estimatedAltitude: 180,
  },
  {
    id: "sakaideOhashi",
    name: "さかいで大橋まつり海上花火大会",
    prefecture: "香川県",
    venueQuery: "坂出市 香川県",
    lat: 34.3166804,
    lng: 133.8604011,
    season: "8月",
    shellInfo: "瀬戸大橋を背景にした海上花火、約4000発",
    estimatedAltitude: 200,
  },
  {
    id: "uwajima",
    name: "宇和島港まつり花火大会",
    prefecture: "愛媛県",
    venueQuery: "宇和島港 愛媛県宇和島市",
    lat: 33.2288465,
    lng: 132.5473123,
    season: "7月",
    shellInfo: "宇和島湾の花火",
    estimatedAltitude: 200,
  },
  {
    id: "hamasaki",
    name: "浜崎祇園祭花火大会",
    prefecture: "佐賀県",
    venueQuery: "浜崎海岸 佐賀県唐津市",
    lat: 33.4495677,
    lng: 130.0323618,
    season: "7月",
    shellInfo: "浜崎海岸の花火",
    estimatedAltitude: 180,
  },
  {
    id: "yoshinogari",
    name: "吉野ヶ里花火大会",
    prefecture: "佐賀県",
    venueQuery: "吉野ヶ里歴史公園 佐賀県神埼郡吉野ヶ里町",
    lat: 33.3270916,
    lng: 130.3842637,
    season: "7月",
    shellInfo: "史跡公園での花火",
    estimatedAltitude: 180,
  },
  {
    id: "amakusa",
    name: "天草市花火大会",
    prefecture: "熊本県",
    venueQuery: "本渡港 熊本県天草市",
    lat: 32.4638590,
    lng: 130.2014942,
    season: "7月",
    shellInfo: "本渡港の花火",
    estimatedAltitude: 180,
  },
  {
    id: "arao",
    name: "あらお花火大会",
    prefecture: "熊本県",
    venueQuery: "荒尾市 熊本県",
    lat: 32.9867584,
    lng: 130.4334027,
    season: "7月",
    shellInfo: "グリーンランド周辺の花火",
    estimatedAltitude: 180,
  },
  {
    id: "kitsuki",
    name: "杵築市納涼花火大会",
    prefecture: "大分県",
    venueQuery: "杵築市 大分県",
    lat: 33.4168490,
    lng: 131.6217599,
    season: "8月16日",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "miyazakiNoryo",
    name: "みやざき納涼花火大会",
    prefecture: "宮崎県",
    venueQuery: "大淀川 宮崎県宮崎市",
    lat: 31.9581515,
    lng: 131.3919024,
    season: "8月1日",
    shellInfo: "70年以上の歴史、1万発超",
    estimatedAltitude: 250,
  },
  {
    id: "kurobe",
    name: "くろべ生地浜海上花火大会",
    prefecture: "富山県",
    venueQuery: "生地浜 富山県黒部市",
    lat: 36.8571484,
    lng: 137.4184466,
    season: "8月",
    shellInfo: "帆船海王丸のライトアップと花火のコラボ",
    estimatedAltitude: 200,
  },
  {
    id: "tsuruga",
    name: "敦賀とうろう流しと大花火大会",
    prefecture: "福井県",
    venueQuery: "気比の松原 福井県敦賀市",
    lat: 35.6555444,
    lng: 136.0489589,
    season: "8月16日",
    shellInfo: "とうろう流しと合わせて約1万発",
    estimatedAltitude: 250,
  },
  {
    id: "echizenMinato",
    name: "越前みなと大花火",
    prefecture: "福井県",
    venueQuery: "越前町 福井県",
    lat: 35.9747717,
    lng: 136.1234893,
    season: "8月",
    shellInfo: "約1万3千発",
    estimatedAltitude: 220,
  },
  {
    id: "aritaKimon",
    name: "紀文まつり花火大会",
    prefecture: "和歌山県",
    venueQuery: "有田市 和歌山県",
    lat: 34.0832764,
    lng: 135.1278229,
    season: "8月10日",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "tanabe",
    name: "田辺花火大会",
    prefecture: "和歌山県",
    venueQuery: "扇ヶ浜 和歌山県田辺市",
    lat: 33.7239550,
    lng: 135.3791070,
    season: "10月（秋開催）",
    shellInfo: "全長1000mの大パノラマ花火、約3500発",
    estimatedAltitude: 220,
  },
  {
    id: "shirahama",
    name: "南紀白浜花火フェスタ",
    prefecture: "和歌山県",
    venueQuery: "白浜町 和歌山県",
    lat: 33.6781748,
    lng: 135.3479399,
    season: "8月10日",
    shellInfo: "約2500発",
    estimatedAltitude: 180,
  },
  {
    id: "izumo",
    name: "出雲市花火大会",
    prefecture: "島根県",
    venueQuery: "出雲市 島根県",
    lat: 35.3668891,
    lng: 132.7548827,
    season: "8月",
    shellInfo: "約4万人が来場",
    estimatedAltitude: 180,
  },
  {
    id: "hamada",
    name: "石州浜っ子夏まつり花火大会",
    prefecture: "島根県",
    venueQuery: "浜田漁港 島根県浜田市",
    lat: 34.8897203,
    lng: 132.0625229,
    season: "8月",
    shellInfo: "浜田漁港での花火",
    estimatedAltitude: 180,
  },
  {
    id: "masuda",
    name: "高津川花火大会（益田水郷祭）",
    prefecture: "島根県",
    venueQuery: "高津川 島根県益田市",
    lat: 34.6961886,
    lng: 131.8208389,
    season: "8月",
    shellInfo: "高津川河口での花火",
    estimatedAltitude: 180,
  },
  {
    id: "kisuki",
    name: "きすき夏まつり",
    prefecture: "島根県",
    venueQuery: "木次駅 雲南市",
    lat: 35.2946568,
    lng: 132.9018604,
    season: "7月下旬",
    shellInfo: "約1500発。斐伊川河川敷公園で、明治初期から続く祭り",
    estimatedAltitude: 150,
  },
  {
    id: "miyako",
    name: "宮古海上花火大会",
    prefecture: "岩手県",
    venueQuery: "宮古港 岩手県宮古市",
    lat: 39.6248774,
    lng: 141.9616630,
    season: "8月12日",
    shellInfo: "海上花火大会",
    estimatedAltitude: 200,
  },
  {
    id: "kitakami",
    name: "北上・みちのく芸能まつり花火大会",
    prefecture: "岩手県",
    venueQuery: "北上市 岩手県",
    lat: 39.2866832,
    lng: 141.1135121,
    season: "8月上旬",
    shellInfo: "芸能まつりに合わせた花火",
    estimatedAltitude: 180,
  },
  {
    id: "takaoka",
    name: "北日本新聞納涼花火（高岡会場）",
    prefecture: "富山県",
    venueQuery: "高岡市 富山県",
    lat: 36.7362147,
    lng: 137.0187306,
    season: "7月",
    shellInfo: "1952年から続く、約1200発",
    estimatedAltitude: 180,
  },
  {
    id: "kanazawaHanabi",
    name: "北國花火 金沢大会",
    prefecture: "石川県",
    venueQuery: "金沢市 石川県",
    lat: 36.5616270,
    lng: 136.6568822,
    season: "7月",
    shellInfo: "約1万2千発",
    estimatedAltitude: 220,
  },
  {
    id: "uchinada",
    name: "内灘町民夏まつり",
    prefecture: "石川県",
    venueQuery: "内灘町 石川県",
    lat: 36.6628934,
    lng: 136.6549747,
    season: "7月",
    shellInfo: "地域の夏祭り花火",
    estimatedAltitude: 180,
  },
  {
    id: "iseJingu",
    name: "伊勢神宮奉納全国花火大会",
    prefecture: "三重県",
    venueQuery: "伊勢市 三重県",
    lat: 34.4875944,
    lng: 136.7092334,
    season: "7月18日",
    shellInfo: "神宮奉納の全国花火大会",
    estimatedAltitude: 200,
  },
  {
    id: "toba",
    name: "鳥羽みなとまつり花火大会",
    prefecture: "三重県",
    venueQuery: "鳥羽港 三重県鳥羽市",
    lat: 34.3911230,
    lng: 136.9075140,
    season: "7月",
    shellInfo: "約3600発",
    estimatedAltitude: 180,
  },
  {
    id: "sakaiUonoyoichi",
    name: "SBI舞花火 in 堺大魚夜市",
    prefecture: "大阪府",
    venueQuery: "堺市堺区 大阪府",
    lat: 34.5778240,
    lng: 135.4986844,
    season: "7月下旬",
    shellInfo: "夜市に合わせた花火",
    estimatedAltitude: 180,
  },
  {
    id: "ibarakiBenten",
    name: "茨木辯天花火大会",
    prefecture: "大阪府",
    venueQuery: "茨木市 大阪府",
    lat: 34.8161302,
    lng: 135.5685528,
    season: "8月",
    shellInfo: "約3000発、大阪府内で高い知名度",
    estimatedAltitude: 180,
  },
  {
    id: "yoshinogawa",
    name: "吉野川祭り納涼花火大会",
    prefecture: "奈良県",
    venueQuery: "大川橋 五條市",
    lat: 34.3480846,
    lng: 135.6959925,
    season: "8月15日",
    shellInfo: "奈良県内最大規模、約4000発。吉野川大川橋上流側河川敷で開催",
    estimatedAltitude: 200,
  },
  {
    id: "sakaiminato",
    name: "境港みなと祭花火大会",
    prefecture: "鳥取県",
    venueQuery: "境水道 鳥取県境港市",
    lat: 35.5479432,
    lng: 133.2450293,
    season: "7月",
    shellInfo: "約1000発、水中花火など",
    estimatedAltitude: 180,
  },
  {
    id: "kurayoshi",
    name: "倉吉打吹まつり 飛天花火大会",
    prefecture: "鳥取県",
    venueQuery: "倉吉市 鳥取県",
    lat: 35.4301660,
    lng: 133.8255250,
    season: "8月",
    shellInfo: "約4000発",
    estimatedAltitude: 180,
  },
  {
    id: "maniwa",
    name: "真庭市花火大会",
    prefecture: "岡山県",
    venueQuery: "旭川 岡山県真庭市",
    lat: 35.1710397,
    lng: 133.7322942,
    season: "7月25日",
    shellInfo: "約2500発",
    estimatedAltitude: 180,
  },
  {
    id: "sasebo",
    name: "鹿町ふるさと夏まつり花火大会",
    prefecture: "長崎県",
    venueQuery: "佐世保市 長崎県",
    lat: 33.1799965,
    lng: 129.7152872,
    season: "8月13日",
    shellInfo: "地域の夏祭り花火",
    estimatedAltitude: 180,
  },
  {
    id: "beppu",
    name: "べっぷ火の海まつり（スパビーチ花火）",
    prefecture: "大分県",
    venueQuery: "別府市 大分県",
    lat: 33.2845752,
    lng: 131.4913063,
    season: "7月",
    shellInfo: "温泉地の海辺で打ち上げる花火",
    estimatedAltitude: 180,
  },
  {
    id: "urasoe",
    name: "浦添てだこまつり花火",
    prefecture: "沖縄県",
    venueQuery: "浦添市 沖縄県",
    lat: 26.2459242,
    lng: 127.7219769,
    season: "期間中（詳細は公式サイト参照）",
    shellInfo: "てだこまつりの花火",
    estimatedAltitude: 150,
  },
  {
    id: "ginowan",
    name: "宜野湾はごろも祭り 花火フェスタ",
    prefecture: "沖縄県",
    venueQuery: "宜野湾海浜公園 沖縄県宜野湾市",
    lat: 26.2811454,
    lng: 127.7345401,
    season: "9月",
    shellInfo: "海浜公園での花火",
    estimatedAltitude: 150,
  },
  {
    id: "fukuyama",
    name: "福山夏まつり あしだ川花火大会",
    prefecture: "広島県",
    venueQuery: "芦田川 広島県福山市",
    lat: 34.4651879,
    lng: 133.3664647,
    season: "8月15日",
    shellInfo: "西日本一の長さ1.4kmの水上スターマイン",
    estimatedAltitude: 220,
  },
  {
    id: "ube",
    name: "宇部市花火大会",
    prefecture: "山口県",
    venueQuery: "宇部港 山口県宇部市",
    lat: 33.9321382,
    lng: 131.2732464,
    season: "7月25日",
    shellInfo: "約3500発",
    estimatedAltitude: 180,
  },
  {
    id: "tadotsu",
    name: "たどつ港まつり花火大会",
    prefecture: "香川県",
    venueQuery: "多度津町 香川県",
    lat: 34.2716875,
    lng: 133.7601435,
    season: "8月1日",
    shellInfo: "港まつりの花火",
    estimatedAltitude: 180,
  },
  {
    id: "imabari",
    name: "今治市民の祭り「おんまく」花火",
    prefecture: "愛媛県",
    venueQuery: "今治市 愛媛県",
    lat: 34.0658182,
    lng: 132.9976758,
    season: "8月",
    shellInfo: "市民祭りの花火",
    estimatedAltitude: 180,
  },
  {
    id: "niihama",
    name: "にいはま納涼花火大会",
    prefecture: "愛媛県",
    venueQuery: "新居浜市 愛媛県",
    lat: 33.9603497,
    lng: 133.2835899,
    season: "8月",
    shellInfo: "高さ40m・長さ400mの大ナイアガラ",
    estimatedAltitude: 180,
  },
  {
    id: "choshi",
    name: "銚子みなとまつり花火大会",
    prefecture: "千葉県",
    venueQuery: "銚子漁港 千葉県銚子市",
    lat: 35.7421899,
    lng: 140.8617063,
    season: "8月8日",
    shellInfo: "約8000発",
    estimatedAltitude: 220,
  },
  {
    id: "tateyama",
    name: "館山観光まつり 館山湾花火大会",
    prefecture: "千葉県",
    venueQuery: "館山湾 千葉県館山市",
    lat: 35.0198631,
    lng: 139.8417336,
    season: "8月8日",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "katsuura",
    name: "かつうら若潮まつり花火大会",
    prefecture: "千葉県",
    venueQuery: "勝浦市 千葉県",
    lat: 35.1521846,
    lng: 140.3207449,
    season: "8月中旬",
    shellInfo: "勝浦会場・興津会場の2会場開催",
    estimatedAltitude: 180,
  },
  {
    id: "sanjo",
    name: "三条夏まつり大花火大会",
    prefecture: "新潟県",
    venueQuery: "三条市 新潟県",
    lat: 37.6361174,
    lng: 138.9613971,
    season: "8月上旬",
    shellInfo: "豪華スターマインや尺玉の連発など約4000発",
    estimatedAltitude: 300,
  },
  {
    id: "oga",
    name: "男鹿日本海花火",
    prefecture: "秋田県",
    venueQuery: "男鹿市 秋田県",
    lat: 39.8866202,
    lng: 139.8473949,
    season: "8月",
    shellInfo: "日本海を舞台にした花火",
    estimatedAltitude: 200,
  },
  {
    id: "yuzawa",
    name: "湯沢市花火大会",
    prefecture: "秋田県",
    venueQuery: "湯沢市 秋田県",
    lat: 39.1643018,
    lng: 140.4957467,
    season: "8月上旬",
    shellInfo: "役内川河川敷、例年約10万人が来場",
    estimatedAltitude: 200,
  },
  {
    id: "yatsushiro",
    name: "やつしろ全国花火競技大会",
    prefecture: "熊本県",
    venueQuery: "八代市 熊本県",
    lat: 32.5081425,
    lng: 130.6020211,
    season: "10月（秋開催）",
    shellInfo: "全国規模の花火競技大会",
    estimatedAltitude: 250,
  },
  {
    id: "tamana",
    name: "玉名納涼花火大会",
    prefecture: "熊本県",
    venueQuery: "玉名市 熊本県",
    lat: 32.9352591,
    lng: 130.5628137,
    season: "8月",
    shellInfo: "地域の夏の花火大会",
    estimatedAltitude: 180,
  },
  {
    id: "isahaya",
    name: "喜々津夏まつり花火打ち上げ",
    prefecture: "長崎県",
    venueQuery: "諫早市 長崎県",
    lat: 32.8434260,
    lng: 130.0530537,
    season: "8月7日",
    shellInfo: "地域の夏祭り花火",
    estimatedAltitude: 180,
  },
  {
    id: "saikai",
    name: "さきと納涼花火大会",
    prefecture: "長崎県",
    venueQuery: "西海市 長崎県",
    lat: 32.9331936,
    lng: 129.6430585,
    season: "8月10日",
    shellInfo: "地域の夏祭り花火",
    estimatedAltitude: 180,
  },
  {
    id: "iki",
    name: "壱岐の島夜空の祭典",
    prefecture: "長崎県",
    venueQuery: "壱岐市 長崎県",
    lat: 33.7500515,
    lng: 129.6913078,
    season: "8月",
    shellInfo: "離島の夏の花火大会",
    estimatedAltitude: 180,
  },
  {
    id: "aburatsu",
    name: "油津港花火大会",
    prefecture: "宮崎県",
    venueQuery: "油津港 宮崎県日南市",
    lat: 31.5695080,
    lng: 131.4023450,
    season: "7月18日",
    shellInfo: "港を舞台にした花火",
    estimatedAltitude: 180,
  },
  {
    id: "kobayashi",
    name: "小林市花火大会",
    prefecture: "宮崎県",
    venueQuery: "小林市 宮崎県",
    lat: 31.9966841,
    lng: 130.9731456,
    season: "8月上旬",
    shellInfo: "本庄川河川敷の花火",
    estimatedAltitude: 180,
  },
  {
    id: "miyakonojo",
    name: "都城市花火大会",
    prefecture: "宮崎県",
    venueQuery: "都城市 宮崎県",
    lat: 31.7196106,
    lng: 131.0612029,
    season: "8月下旬",
    shellInfo: "高崎総合公園陸上競技場周辺の花火",
    estimatedAltitude: 180,
  },
  {
    id: "nakatsuGion",
    name: "中津祇園市民花火大会",
    prefecture: "大分県",
    venueQuery: "中津市 大分県",
    lat: 33.5982794,
    lng: 131.1883225,
    season: "5月下旬",
    shellInfo: "三隈川周辺の花火",
    estimatedAltitude: 180,
  },
  {
    id: "ogawara",
    name: "おおがわら夏まつり",
    prefecture: "宮城県",
    venueQuery: "大河原町 宮城県",
    lat: 38.0492675,
    lng: 140.7309539,
    season: "8月上旬",
    shellInfo: "地域の夏祭り花火",
    estimatedAltitude: 180,
  },
  {
    id: "wakuya",
    name: "わくや夏まつり",
    prefecture: "宮城県",
    venueQuery: "涌谷町 宮城県",
    lat: 38.5396261,
    lng: 141.1281056,
    season: "7月下旬",
    shellInfo: "地域の夏祭り花火",
    estimatedAltitude: 180,
  },
  {
    id: "hirosaki",
    name: "弘前ねぷたまつり 花火大会",
    prefecture: "青森県",
    venueQuery: "弘前市 青森県",
    lat: 40.6030543,
    lng: 140.4640389,
    season: "8月7日",
    shellInfo: "ねぷた運行に合わせた海上花火",
    estimatedAltitude: 180,
  },
  {
    id: "ichinosekiKawabiraki",
    name: "一関夏まつり 磐井川川開き花火大会",
    prefecture: "岩手県",
    venueQuery: "一関市 岩手県",
    lat: 38.9346961,
    lng: 141.1266050,
    season: "8月第1金曜日",
    shellInfo: "約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "oshu",
    name: "奥州水沢の花火大会",
    prefecture: "岩手県",
    venueQuery: "奥州市 岩手県",
    lat: 39.1442750,
    lng: 141.1392382,
    season: "8月5日",
    shellInfo: "約9000発",
    estimatedAltitude: 220,
  },
  {
    id: "sakataHanabi",
    name: "酒田花火ショー",
    prefecture: "山形県",
    venueQuery: "酒田市 山形県",
    lat: 38.9147229,
    lng: 139.8364101,
    season: "9月（秋開催）",
    shellInfo: "約1万2千発",
    estimatedAltitude: 250,
  },
  {
    id: "oishida",
    name: "大石田まつり 最上川花火大会",
    prefecture: "山形県",
    venueQuery: "大石田町 山形県",
    lat: 38.5938409,
    lng: 140.3726010,
    season: "8月16日",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "tonegawa",
    name: "利根川大花火大会",
    prefecture: "茨城県",
    venueQuery: "境町 茨城県",
    lat: 36.1083794,
    lng: 139.7951982,
    season: "9月中旬",
    shellInfo: "約3万発",
    estimatedAltitude: 300,
  },
  {
    id: "oarai",
    name: "大洗海上花火大会",
    prefecture: "茨城県",
    venueQuery: "大洗町 茨城県",
    lat: 36.3133375,
    lng: 140.5747222,
    season: "9月下旬",
    shellInfo: "約1万5千発",
    estimatedAltitude: 250,
  },
  {
    id: "motegi",
    name: "花火の祭典“夏”",
    prefecture: "栃木県",
    venueQuery: "茂木町 栃木県",
    lat: 36.5321640,
    lng: 140.1874057,
    season: "8月14日",
    shellInfo: "約1万4千発",
    estimatedAltitude: 250,
  },
  {
    id: "himi",
    name: "ひみまつり",
    prefecture: "富山県",
    venueQuery: "氷見市 富山県",
    lat: 36.8645966,
    lng: 136.9704027,
    season: "8月第1土曜日",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "mizuhashi",
    name: "水橋橋まつり",
    prefecture: "富山県",
    venueQuery: "富山市 富山県",
    lat: 36.6957569,
    lng: 137.2136215,
    season: "7月22日",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "kahoku",
    name: "北國大花火 川北大会",
    prefecture: "石川県",
    venueQuery: "能美郡川北町 石川県",
    lat: 36.4659946,
    lng: 136.5530991,
    season: "8月第1土曜日",
    shellInfo: "約1万8千発",
    estimatedAltitude: 250,
  },
  {
    id: "wajima",
    name: "輪島市民まつり花火大会",
    prefecture: "石川県",
    venueQuery: "輪島市 石川県",
    lat: 37.3909384,
    lng: 136.8995701,
    season: "6月第1週",
    shellInfo: "約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "takahama",
    name: "若狭たかはま漁火想",
    prefecture: "福井県",
    venueQuery: "大飯郡高浜町 福井県",
    lat: 35.4852813,
    lng: 135.5597891,
    season: "7月最終週",
    shellInfo: "水中花火とドラゴン花火等、合計約5万発",
    estimatedAltitude: 220,
  },
  {
    id: "yamanakako",
    name: "山中湖報湖祭",
    prefecture: "山梨県",
    venueQuery: "山中湖村 山梨県",
    lat: 35.4106073,
    lng: 138.8610756,
    season: "8月1日",
    shellInfo: "約8000発",
    estimatedAltitude: 200,
  },
  {
    id: "naganogawaChunichi",
    name: "全国選抜長良川中日花火大会",
    prefecture: "岐阜県",
    venueQuery: "岐阜市 岐阜県",
    lat: 35.4230949,
    lng: 136.7627526,
    season: "7月末",
    shellInfo: "約3万発",
    estimatedAltitude: 300,
  },
  {
    id: "ena",
    name: "恵那納涼水上花火大会",
    prefecture: "岐阜県",
    venueQuery: "恵那市 岐阜県",
    lat: 35.4492674,
    lng: 137.4127030,
    season: "7月第4土曜日",
    shellInfo: "約2000発",
    estimatedAltitude: 180,
  },
  {
    id: "abekawa",
    name: "安倍川花火大会",
    prefecture: "静岡県",
    venueQuery: "静岡市 静岡県",
    lat: 34.9751974,
    lng: 138.3831697,
    season: "7月最終土曜日",
    shellInfo: "約1万5千発",
    estimatedAltitude: 250,
  },
  {
    id: "anjin",
    name: "按針祭 海の花火大会",
    prefecture: "静岡県",
    venueQuery: "伊東市 静岡県",
    lat: 34.9656621,
    lng: 139.1017290,
    season: "8月10日",
    shellInfo: "約1万3千発",
    estimatedAltitude: 250,
  },
  {
    id: "toyohashiGion",
    name: "豊橋祇園祭",
    prefecture: "愛知県",
    venueQuery: "豊橋市 愛知県",
    lat: 34.7691230,
    lng: 137.3914610,
    season: "7月第3金・土曜日",
    shellInfo: "約1万2千発",
    estimatedAltitude: 220,
  },
  {
    id: "kariya",
    name: "刈谷わんさか祭り花火大会",
    prefecture: "愛知県",
    venueQuery: "刈谷市 愛知県",
    lat: 34.9746780,
    lng: 137.0027910,
    season: "8月中旬",
    shellInfo: "約7000発",
    estimatedAltitude: 200,
  },
  {
    id: "yokkaichi",
    name: "四日市花火大会",
    prefecture: "三重県",
    venueQuery: "四日市市 三重県",
    lat: 34.9445241,
    lng: 136.6073895,
    season: "8月24日",
    shellInfo: "約4000発",
    estimatedAltitude: 180,
  },
  {
    id: "hisai",
    name: "久居花火大会",
    prefecture: "三重県",
    venueQuery: "津市 三重県",
    lat: 34.7341973,
    lng: 136.5153283,
    season: "8月第1土曜日",
    shellInfo: "約4000発",
    estimatedAltitude: 180,
  },
  {
    id: "aichigawa",
    name: "愛知川祇園納涼祭花火大会",
    prefecture: "滋賀県",
    venueQuery: "愛知郡愛荘町 滋賀県",
    lat: 35.1698878,
    lng: 136.2109940,
    season: "7月第3週",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "otsuShiga",
    name: "大津志賀花火大会",
    prefecture: "滋賀県",
    venueQuery: "大津市 滋賀県",
    lat: 35.0177852,
    lng: 135.8548122,
    season: "7月第4土曜日",
    shellInfo: "約2000発",
    estimatedAltitude: 180,
  },
  {
    id: "kyotoGeijutsu",
    name: "京都芸術花火",
    prefecture: "京都府",
    venueQuery: "京都市伏見区 京都府",
    lat: 34.9694415,
    lng: 135.7662464,
    season: "5月下旬",
    shellInfo: "約1万3千発",
    estimatedAltitude: 250,
  },
  {
    id: "uji",
    name: "宇治川花火大会",
    prefecture: "京都府",
    venueQuery: "宇治市 京都府",
    lat: 34.8851240,
    lng: 135.7995651,
    season: "8月上旬",
    shellInfo: "約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "plHanabi",
    name: "教祖祭PL花火芸術",
    prefecture: "大阪府",
    venueQuery: "富田林市 大阪府",
    lat: 34.4998098,
    lng: 135.5972946,
    season: "8月1日",
    shellInfo: "約2万発",
    estimatedAltitude: 280,
  },
  {
    id: "tenjin",
    name: "天神祭奉納花火",
    prefecture: "大阪府",
    venueQuery: "大阪市北区 大阪府",
    lat: 34.7022017,
    lng: 135.5002725,
    season: "7月25日",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "katsuragi",
    name: "葛城市花火大会",
    prefecture: "奈良県",
    venueQuery: "葛城市 奈良県",
    lat: 34.4888006,
    lng: 135.7266816,
    season: "7月第3日曜日",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "starlight",
    name: "スターライトイリュージョン",
    prefecture: "和歌山県",
    venueQuery: "海南市 和歌山県",
    lat: 34.1548656,
    lng: 135.2092791,
    season: "7月中旬〜9月中旬",
    shellInfo: "約6000発",
    estimatedAltitude: 200,
  },
  {
    id: "wakayamaMinato",
    name: "和歌山港まつり花火大会",
    prefecture: "和歌山県",
    venueQuery: "和歌山市 和歌山県",
    lat: 34.2342931,
    lng: 135.1783549,
    season: "7月20日",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "yurihamaSuigo",
    name: "水郷祭湖上大花火",
    prefecture: "鳥取県",
    venueQuery: "東伯郡湯梨浜町 鳥取県",
    lat: 35.4767260,
    lng: 133.8469330,
    season: "7月第3週",
    shellInfo: "約2000発",
    estimatedAltitude: 180,
  },
  {
    id: "kasaoka",
    name: "笠岡港まつり花火大会",
    prefecture: "岡山県",
    venueQuery: "笠岡市 岡山県",
    lat: 34.5071461,
    lng: 133.5074654,
    season: "8月第1週",
    shellInfo: "約5500発",
    estimatedAltitude: 200,
  },
  {
    id: "kagamino",
    name: "鏡野町大納涼祭",
    prefecture: "岡山県",
    venueQuery: "苫田郡鏡野町 岡山県",
    lat: 35.2860234,
    lng: 133.8715684,
    season: "8月第1週",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "miyoshiHiroshima",
    name: "みよし市民納涼花火大会",
    prefecture: "広島県",
    venueQuery: "三次市 広島県",
    lat: 34.8102400,
    lng: 132.8516910,
    season: "7月25日",
    shellInfo: "約1万6千発",
    estimatedAltitude: 250,
  },
  {
    id: "miyajima",
    name: "宮島水中花火大会",
    prefecture: "広島県",
    venueQuery: "廿日市市宮島町 広島県",
    lat: 34.3543391,
    lng: 132.3383852,
    season: "8月11日",
    shellInfo: "約5000発、水中花火",
    estimatedAltitude: 200,
  },
  {
    id: "hagi",
    name: "萩・日本海大花火大会",
    prefecture: "山口県",
    venueQuery: "萩市 山口県",
    lat: 34.4074815,
    lng: 131.3991940,
    season: "8月1日",
    shellInfo: "約8000発",
    estimatedAltitude: 220,
  },
  {
    id: "nagato",
    name: "ながと仙崎花火大会",
    prefecture: "山口県",
    venueQuery: "長門市 山口県",
    lat: 34.3708941,
    lng: 131.1821587,
    season: "7月下旬",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "komatsushima",
    name: "小松島港まつり花火大会",
    prefecture: "徳島県",
    venueQuery: "小松島市 徳島県",
    lat: 34.0044235,
    lng: 134.5906577,
    season: "海の日",
    shellInfo: "約2000発",
    estimatedAltitude: 180,
  },
  {
    id: "marugame",
    name: "まるがめ婆娑羅まつり花火大会",
    prefecture: "香川県",
    venueQuery: "丸亀市 香川県",
    lat: 34.2888128,
    lng: 133.7982421,
    season: "8月",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "shodoshima",
    name: "小豆島まつり",
    prefecture: "香川県",
    venueQuery: "小豆郡小豆島町 香川県",
    lat: 34.4865787,
    lng: 134.0619398,
    season: "8月15日",
    shellInfo: "約2000発",
    estimatedAltitude: 180,
  },
  {
    id: "shikokuchuo",
    name: "みなと祭り・天神祭り花火大会",
    prefecture: "愛媛県",
    venueQuery: "四国中央市 愛媛県",
    lat: 33.9807440,
    lng: 133.5499338,
    season: "7月25日",
    shellInfo: "約5500発",
    estimatedAltitude: 200,
  },
  {
    id: "sukumo",
    name: "市民祭 宿毛まつり",
    prefecture: "高知県",
    venueQuery: "宿毛市 高知県",
    lat: 32.9390595,
    lng: 132.7262671,
    season: "10月第2日曜日",
    shellInfo: "約4000発",
    estimatedAltitude: 180,
  },
  {
    id: "muroto",
    name: "ふるさと室戸まつり",
    prefecture: "高知県",
    venueQuery: "室戸市 高知県",
    lat: 33.2898523,
    lng: 134.1522806,
    season: "7月第3週",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "chikugogawa",
    name: "筑後川花火大会",
    prefecture: "福岡県",
    venueQuery: "久留米市 福岡県",
    lat: 33.3196545,
    lng: 130.5080625,
    season: "8月5日",
    shellInfo: "約1万5千発",
    estimatedAltitude: 250,
  },
  {
    id: "momochi",
    name: "シーサイドももち花火ファンタジアFUKUOKA",
    prefecture: "福岡県",
    venueQuery: "福岡市早良区 福岡県",
    lat: 33.5938359,
    lng: 130.3495139,
    season: "7月下旬",
    shellInfo: "約1万2千発",
    estimatedAltitude: 250,
  },
  {
    id: "imari",
    name: "伊万里市民納涼花火大会",
    prefecture: "佐賀県",
    venueQuery: "伊万里市 佐賀県",
    lat: 33.2644557,
    lng: 129.8808439,
    season: "8月第2週",
    shellInfo: "約3000発",
    estimatedAltitude: 180,
  },
  {
    id: "kawakami",
    name: "まほろば納涼まつり 川上峡花火大会",
    prefecture: "佐賀県",
    venueQuery: "佐賀市 佐賀県",
    lat: 33.2639134,
    lng: 130.3008378,
    season: "7月25日",
    shellInfo: "約2200発",
    estimatedAltitude: 180,
  },
  {
    id: "tsukumi",
    name: "つくみ港まつり納涼花火大会",
    prefecture: "大分県",
    venueQuery: "津久見市 大分県",
    lat: 33.0722942,
    lng: 131.8613470,
    season: "7月第3日曜日",
    shellInfo: "約5500発",
    estimatedAltitude: 200,
  },
  {
    id: "hita",
    name: "日田川開き観光祭",
    prefecture: "大分県",
    venueQuery: "日田市 大分県",
    lat: 33.3211655,
    lng: 130.9411316,
    season: "5月下旬",
    shellInfo: "各日約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "ryukyuKaien",
    name: "琉球海炎祭",
    prefecture: "沖縄県",
    venueQuery: "宜野湾市 沖縄県",
    lat: 26.2814968,
    lng: 127.7784916,
    season: "4月第2週",
    shellInfo: "約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "adachi",
    name: "足立の花火",
    prefecture: "東京都",
    venueQuery: "西新井橋 足立区",
    lat: 35.7585839,
    lng: 139.7893102,
    season: "5月最終土曜日",
    shellInfo: "約1万3000発を1時間に凝縮して打ち上げ",
    estimatedAltitude: 300,
  },
  {
    id: "hachioji",
    name: "八王子花火大会",
    prefecture: "東京都",
    venueQuery: "富士森公園 八王子市",
    lat: 35.6517618,
    lng: 139.3233841,
    season: "8月上旬",
    shellInfo: "約4000発",
    estimatedAltitude: 250,
  },
  {
    id: "ome",
    name: "青梅市納涼花火大会",
    prefecture: "東京都",
    venueQuery: "永山公園 青梅市",
    lat: 35.7933667,
    lng: 139.2655629,
    season: "8月上旬",
    shellInfo: "尺五寸玉（15号）あり。都内最大級の大玉",
    estimatedAltitude: 450,
    note: "都内では珍しい山あいの会場（永山丘陵）で打ち上げ。",
  },
  {
    id: "koto",
    name: "江東花火大会",
    prefecture: "東京都",
    venueQuery: "荒川砂町水辺公園",
    lat: 35.6662021,
    lng: 139.8434311,
    season: "8月中旬",
    shellInfo: "約6000発。打ち上げ場所と観覧エリアがごく近い",
    estimatedAltitude: 200,
  },
  {
    id: "chofu",
    name: "調布花火",
    prefecture: "東京都",
    venueQuery: "京王多摩川駅 調布市",
    lat: 35.6449852,
    lng: 139.5371578,
    season: "9月中旬（近年は秋開催）",
    shellInfo: "約1万発。大玉50連発あり",
    estimatedAltitude: 300,
    note: "旧称「映画のまち調布“夏”花火」。多摩川河川敷の布田会場ほか複数会場で開催。",
  },
  {
    id: "tamagawa_setagaya",
    name: "世田谷区たまがわ花火大会",
    prefecture: "東京都",
    venueQuery: "二子玉川緑地運動場 東京都世田谷区",
    lat: 35.6151844,
    lng: 139.6172493,
    season: "10月上旬",
    shellInfo: "最大6号玉(2026年から10号より縮小)。対岸の川崎会場と同時打ち上げで合計約6000発",
    estimatedAltitude: 220,
    note: "多摩川左岸・二子橋上流の世田谷区鎌田一丁目付近が会場。対岸の「川崎市制記念多摩川花火大会」(tamagawa_kawasaki)と同日同時刻開催。",
  },
  {
    id: "tamagawa_kawasaki",
    name: "川崎市制記念多摩川花火大会",
    prefecture: "神奈川県",
    venueQuery: "多摩川緑地瀬田周辺エリア 神奈川県川崎市高津区",
    lat: 35.6080845,
    lng: 139.6238782,
    season: "10月上旬",
    shellInfo: "最大6号玉(2026年から10号より縮小)。対岸の世田谷会場と同時打ち上げで合計約6000発",
    estimatedAltitude: 220,
    note: "多摩川右岸・二子橋〜第三京浜間の川崎市高津区瀬田付近が会場。対岸の「世田谷区たまがわ花火大会」(tamagawa_setagaya)と同日同時刻開催。",
  },
  {
    id: "tokyowanTaikakasai",
    name: "東京湾大華火祭",
    prefecture: "東京都",
    venueQuery: "晴海埠頭 中央区",
    lat: 35.6477468,
    lng: 139.7748283,
    season: "10月（2026年は10/24。財政上の理由で2015年から休止していたが11年ぶりに復活）",
    shellInfo: "約1万2000発。海上打ち上げの大規模大会",
    estimatedAltitude: 300,
    note: "観覧は事前申込制(チケット制)、自由観覧不可。",
  },
  {
    id: "zushiKaigan",
    name: "逗子海岸花火大会",
    prefecture: "神奈川県",
    venueQuery: "逗子海岸 神奈川県逗子市",
    lat: 35.2910833,
    lng: 139.5730374,
    season: "5月下旬",
    shellInfo: "約7000発。海水浴シーズン前の海上花火",
    estimatedAltitude: 200,
  },
  {
    id: "hiratsukaShonan",
    name: "湘南ひらつか花火大会",
    prefecture: "神奈川県",
    venueQuery: "平塚海岸 神奈川県平塚市",
    lat: 35.3274105,
    lng: 139.3492322,
    season: "8月下旬",
    shellInfo: "約3000発",
    estimatedAltitude: 150,
  },
  {
    id: "atsugiAyu",
    name: "あつぎ鮎まつり大花火大会",
    prefecture: "神奈川県",
    venueQuery: "相模大堰 神奈川県厚木市",
    lat: 35.4201384,
    lng: 139.3708884,
    season: "10月",
    shellInfo: "約1万発、県内最大級",
    estimatedAltitude: 250,
  },
  {
    id: "sagamiharaNoryo",
    name: "相模原納涼花火大会",
    prefecture: "神奈川県",
    venueQuery: "高田橋 神奈川県相模原市",
    lat: 35.5412478,
    lng: 139.3301548,
    season: "9月上旬",
    shellInfo: "約1万発",
    estimatedAltitude: 220,
  },
  {
    id: "yokohamaMinatomirai",
    name: "横浜みなとみらいスマートフェスティバル",
    prefecture: "神奈川県",
    venueQuery: "赤レンガパーク 神奈川県横浜市",
    lat: 35.4526171,
    lng: 139.6432299,
    season: "8月下旬",
    shellInfo: "約1万8000発、県内最大級",
    estimatedAltitude: 300,
    note: "旧称「神奈川新聞花火大会」。",
  },
  {
    id: "yokohamaKanazawa",
    name: "金沢まつり花火大会",
    prefecture: "神奈川県",
    venueQuery: "海の公園 神奈川県横浜市金沢区",
    lat: 35.3382607,
    lng: 139.6339053,
    season: "8月下旬",
    shellInfo: "約3500発",
    estimatedAltitude: 150,
  },
  {
    id: "chigasakiSouthern",
    name: "サザンビーチちがさき花火大会",
    prefecture: "神奈川県",
    venueQuery: "サザンビーチちがさき 神奈川県茅ヶ崎市",
    lat: 35.3171877,
    lng: 139.3980517,
    season: "8月上旬",
    shellInfo: "約2000発",
    estimatedAltitude: 120,
  },
  {
    id: "yokosukaKaikoku",
    name: "よこすか開国花火大会",
    prefecture: "神奈川県",
    venueQuery: "うみかぜ公園 神奈川県横須賀市",
    lat: 35.2767865,
    lng: 139.6827049,
    season: "開催時期は年により変動",
    shellInfo: "約5000発",
    estimatedAltitude: 200,
  },
  {
    id: "hakoneAshinoko",
    name: "芦ノ湖夏まつりウィーク湖水祭花火大会",
    prefecture: "神奈川県",
    venueQuery: "芦ノ湖 神奈川県足柄下郡箱根町",
    lat: 35.2144830,
    lng: 139.0019966,
    season: "7月下旬",
    shellInfo: "約5000発。芦ノ湖上の湖上花火",
    estimatedAltitude: 200,
  },
  {
    id: "himejiMinato",
    name: "姫路みなと祭海上花火大会",
    prefecture: "兵庫県",
    venueQuery: "姫路港 兵庫県姫路市",
    lat: 34.7846750,
    lng: 134.6629111,
    season: "9月中旬〜下旬",
    shellInfo: "1万発超、尺玉あり",
    estimatedAltitude: 300,
  },
  {
    id: "katakaiMatsuri",
    name: "片貝まつり（浅原神社秋季例大祭奉納大煙火）",
    prefecture: "新潟県",
    venueQuery: "片貝町 新潟県小千谷市",
    lat: 37.3684650,
    lng: 138.7802390,
    season: "9月上旬〜中旬",
    shellInfo: "世界最大級の四尺玉が打ち上げられることで知られる",
    estimatedAltitude: 750,
    note: "四尺玉は毎年ではなく数年に一度の打ち上げ。通常演目は尺玉・三尺玉クラス中心。",
  },
  {
    id: "kogaHanabi",
    name: "古河花火大会",
    prefecture: "茨城県",
    venueQuery: "古河ゴルフリンクス 茨城県古河市",
    lat: 36.2001969,
    lng: 139.6926039,
    season: "8月上旬",
    shellInfo: "大型スターマイン中心、来場者約20万人",
    estimatedAltitude: 250,
  },
  {
    id: "oyamaHanabi",
    name: "小山の花火",
    prefecture: "栃木県",
    venueQuery: "観晃橋 栃木県小山市",
    lat: 36.3181513,
    lng: 139.7979430,
    season: "10月",
    shellInfo: "約2万発",
    estimatedAltitude: 280,
  },
  {
    id: "josoKinugawa",
    name: "常総きぬ川花火大会",
    prefecture: "茨城県",
    venueQuery: "橋本運動公園 茨城県常総市",
    lat: 36.0263509,
    lng: 139.9862822,
    season: "9月中旬",
    shellInfo: "約2万2000発",
    estimatedAltitude: 250,
  },
  {
    id: "inuyamaNihonLine",
    name: "日本ライン夏まつりロングラン花火",
    prefecture: "愛知県",
    venueQuery: "犬山橋 愛知県犬山市",
    lat: 35.3929035,
    lng: 136.9454601,
    season: "8月上旬（複数日連夜開催）",
    shellInfo: "1夜あたり約300発、連夜開催が特徴",
    estimatedAltitude: 120,
  },
];
